import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';

import Button from '@mui/material/Button';
import CreateTwoToneIcon from '@mui/icons-material/CreateTwoTone';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { styled } from '@mui/material/styles';
import { createSvgIcon } from '@mui/material/utils';
import { generateClient } from 'aws-amplify/api';
import {
  DataGrid,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  ExportPrint,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  QuickFilterTrigger,
  GridRowModes,
  gridExpandedSortedRowIdsSelector,
  useGridApiContext, GridActionsCellItem
} from '@mui/x-data-grid';
import { claimByActiveStatusAndRetroGroup } from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';
import { listClaims } from '../../graphql/queries';
import RowPopUp from '../RowPopUp';
import DatabasePopUp from '../DatabasePopUp';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Badge from '@mui/material/Badge';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { buildCutoffFilter } from '../../utils/buildCutoffFilters';
import { getClaimsManagerDisplayName } from '../../utils/claimsManagerMapping';


const getFilteredRows = ({ apiRef }) => gridExpandedSortedRowIdsSelector(apiRef);
const client = generateClient({authMode: 'userPool'});

const ExportIcon = createSvgIcon(
  <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
  'SaveAlt',
);
const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState.expanded ? 260 : 'var(--trigger-width)',
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(['width', 'opacity']),
}));

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  width: 'min-content',
  height: 'min-content',
  zIndex: 1,
  opacity: ownerState.expanded ? 0 : 1,
  pointerEvents: ownerState.expanded ? 'none' : 'auto',
  transition: theme.transitions.create(['opacity']),
}));

function CustomToolbar() {
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
  const exportMenuTriggerRef = React.useRef(null);

  return (
    <Toolbar>
      <Typography fontWeight="medium" sx={{ flex: 1, mx: 0.5 }}>
        Toolbar
      </Typography>

      <Tooltip title="Columns">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>

      <Tooltip title="Filters">
        <FilterPanelTrigger
          render={(props, state) => (
            <ToolbarButton {...props} color="default">
              <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                <FilterListIcon fontSize="small" />
              </Badge>
            </ToolbarButton>
          )}
        />
      </Tooltip>

      <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
      <Tooltip title="Export">
        <ToolbarButton
          ref={exportMenuTriggerRef}
          id="export-menu-trigger"
          aria-controls="export-menu"
          aria-haspopup="true"
          aria-expanded={exportMenuOpen ? 'true' : undefined}
          onClick={() => setExportMenuOpen(true)}
        >
          <FileDownloadIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>

      <Menu
        id="export-menu"
        anchorEl={exportMenuTriggerRef.current}
        open={exportMenuOpen}
        onClose={() => setExportMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            'aria-labelledby': 'export-menu-trigger',
          },
        }}
      >
        <ExportPrint render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
          Print
        </ExportPrint>
        <ExportCsv render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
          Download as CSV
        </ExportCsv>
        {/* Available to MUI X Premium users */}
        {/* <ExportExcel render={<MenuItem />}>
           Download as Excel
          </ExportExcel> */}
      </Menu>

      <StyledQuickFilter>
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title="Search" enterDelay={0}>
              <StyledToolbarButton
                {...triggerProps}
                ownerState={{ expanded: state.expanded }}
                color="default"
                aria-disabled={state.expanded}
              >
                <SearchIcon fontSize="small" />
              </StyledToolbarButton>
            </Tooltip>
          )}
        />
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              ownerState={{ expanded: state.expanded }}
              inputRef={ref}
              aria-label="Search"
              placeholder="Search..."
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label="Clear search"
                        material={{ sx: { marginRight: -0.75 } }}
                      >
                        <CancelIcon fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...controlProps.slotProps?.input,
                },
                ...controlProps.slotProps,
              }}
            />
          )}
        />
      </StyledQuickFilter>
    </Toolbar>
  );
}



const ClaimsTable = () => {
  // Claim state details
  const [claims, setClaims] = useState([]);

  // Data grid state details
  const [rows, setRows] = React.useState(claims);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [databasePopUpOpen, setDatabasePopUpOpen] = useState(false);
  const [rowPopUpOpen, setRowPopUpOpen] = useState(false);

  const handleDatabasePopUpClose = () => {
    setDatabasePopUpOpen(false);
  };

  const handleRowPopUpClose = () => {
    setRowPopUpOpen(false);
  };

  useEffect(() => {
    fetchClaims();
  }, []);


const fetchClaims = async () => {
  try {
    let allClaims = [];
    let nextToken = null;
    const cutOffFilter = buildCutoffFilter();
    const filter = {
      and: [
        {or : [
        { retroGroup: { eq: 'Zirkle' } },
        { retroGroup: {eq: 'zirkle'} }
        ]
        },
        cutOffFilter,
        { not: {
            or: [
              { claimStatus: { eq: 'REJECTED' } },
              { claimStatus: { eq: 'CLOSED' } },
              { followUpMgr: { eq: 'SKIP'    } },
              { followUpMgr: { eq: 'RESOLVED'} }
            ]
        } }
      ]
    }; 

    do {
      const { data } = await client.graphql({
        query: listClaims,
        variables: { filter, nextToken },
      });

      allClaims.push(...data.listClaims.items);
      nextToken = data.listClaims.nextToken;   // handle pagination  :contentReference[oaicite:1]{index=1}
    } while (nextToken);

    setClaims(allClaims);
    console.log('Total claims fetched:', allClaims.length);
  } catch (err) {
    console.error('Error fetching claims', err);
  }
};

  const updateClaim = async (newRow) => {
    try {
      const claim = newRow;
      delete newRow.updatedAt;
      delete newRow.createdAt;
      delete newRow.isNew;
      delete newRow.__typename;
      console.log(claim);

      const claimsData = await client.graphql({
        query: mutations.updateClaim,
        variables: { input: claim }
      });
      console.log('database updated');
    }
    catch (e) {
      console.log('error when updating claims: ', e);
      setDatabasePopUpOpen(true); // Open the dialog here
    }
  }

  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleResolveClick = (id) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);

    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = newRow => {
    updateClaim(newRow);
    const updatedRow = { ...newRow, isNew: false };
    try{
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    console.log('row updated');
    return updatedRow;
    }
    catch (error) {
      console.log('error when updating row: ', error);
      setRowPopUpOpen(true); // Open the dialog here
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  // Define column attributes for data grid creation
  const columns = [
    { field: 'planYear', headerClassName: 'timeloss-app-theme--header', headerName: 'Plan Year', width: 115, editable: true },
    // { field: 'account', headerClassName: 'timeloss-app-theme--header', headerName: 'Account ID', width: 100, editable: true },
    { field: 'dba', headerClassName: 'timeloss-app-theme--header', headerName: 'DBA', width: 160, editable: true },
    { field: 'claimID', headerClassName: 'timeloss-app-theme--header', headerName: 'Claim ID', width: 120, editable: false },
    { field: 'workerName', headerClassName: 'timeloss-app-theme--header', headerName: 'Claimant Name', width: 150, editable: true },
{
    field: 'injuryDate',
    headerName: 'Injury Date',
    headerClassName: 'timeloss-app-theme--header',
    width: 115,
    editable: 'false'
  },
    { field: 'claimStatus', headerClassName: 'timeloss-app-theme--header', headerName: 'Claim Status', width: 105, editable: true },
    { 
      field: 'claimsManager', 
      headerClassName: 'timeloss-app-theme--header', 
      headerName: 'Claims Manager', 
      width: 140, 
      editable: true,
      renderCell: (params) => {
        return <span>{getClaimsManagerDisplayName(params.value)}</span>;
      }
    },
    {
      field: 'selectedMaCost',
      headerClassName: 'timeloss-app-theme--header',
      headerName: 'Incurred Medical Aid',
      minWidth: 150,
      type: 'number',
      editable: false
    },
    {
      field: 'selectedPensionCost',
      headerClassName: 'timeloss-app-theme--header',
      headerName: 'Incurred Pension Cost',
      type: 'number',
      minWidth: 170,
      editable: false
    },    
    {
      field: 'selectedPpdCost',
      headerClassName: 'timeloss-app-theme--header',
      headerName: 'Incurred PPD Cost',
      type: 'number',
      minWidth: 150,
      editable: false
    },    
    {
      field: 'selectedTlCost',
      headerClassName: 'timeloss-app-theme--header',
      headerName: 'Incurred Time-Loss Cost',
      type: 'number',
      minWidth: 190,
      editable: false
    },
        {
      field: 'totalCost',
      headerClassName: 'timeloss-app-theme--header',
      headerName: 'Incurred Total Cost',
      type: 'number',
      minWidth: 150,
      editable: false
    },

        // { field: 'activeStatus', headerClassName: 'timeloss-app-theme--header', headerName: "Active TL", width: 100, editable: true },
    // { field: 'retroGroup', headerClassName: 'timeloss-app-theme--header', headerName: 'Member Type', width: 110, editable: true },
    { field: 'followUpMgr', headerClassName: 'timeloss-app-theme--header', headerName: 'Assigned To', width: 110, editable: true },
    { field: 'kosAnalysis', headerClassName: 'timeloss-app-theme--header', headerName: 'KOS Analysis', width: 125, editable: true },
    { field: 'followupFlag', headerClassName: 'timeloss-app-theme--header', headerName: 'Flag for Follow-Up', width: 140, editable: true },
    {
      field: 'toDo',
      headerClassName: 'timeloss-app-theme--header',
      headerName: 'Actions',
      width: 300,
      editable: true
    },
        {
      field: 'notes',
      headerClassName: 'timeloss-app-theme--header',
      headerName: 'Notes',
      width: 300,
      editable: true
    },
  ];

  return (
    <div className="claimsTable">

<DatabasePopUp open={databasePopUpOpen} handleClose={handleDatabasePopUpClose} />

<RowPopUp open={rowPopUpOpen} handleClose={handleRowPopUpClose} />
      <Box
        sx={{
          height: 900,
          width: '100%',
          '& .actions': {
            color: 'text.secondary',
          },
          '& .textPrimary': {
            color: 'text.primary',
          },
          '& .even': {
            backgroundColor: '#fcfaf5',
          },
          '& .odd': {
            backgroundColor: 'white',
          },
          '& .timeloss-app-theme--header': {
            fontWeight: 'bold',
            backgroundColor: '#B0D876',
            color: 'black'
          }
        }}
      >
        <DataGrid
          rows={claims}
          getRowHeight={() => 'auto'}
          columns={columns}
          // editMode="row"
          getRowId={(claims) => claims.uniqueID}
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStart={handleRowEditStart}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'}
        slots={{ toolbar: CustomToolbar }}
        showToolbar
        />
      </Box>
    </div>
  )
}

export default ClaimsTable;